import { useState } from "react";
import Card, { SectionTitle } from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Modal from "@/components/base/Modal";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import { Input, Select } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { addCategory, deleteCategory, updateCategory } from "@/services/financeService";
import type { Cycle, FinanceCategory, TransactionType } from "@/types";

const ICONS = [
  "ri-wallet-3-line",
  "ri-pencil-ruler-2-line",
  "ri-flashlight-line",
  "ri-water-flash-line",
  "ri-wifi-line",
  "ri-brush-line",
  "ri-tools-line",
  "ri-computer-line",
  "ri-bus-2-line",
  "ri-gas-station-line",
  "ri-book-2-line",
  "ri-file-list-3-line",
  "ri-restaurant-2-line",
  "ri-hand-heart-line",
  "ri-more-line",
];

interface Props {
  type: TransactionType;
  categories: FinanceCategory[];
  cycles: Cycle[];
  onChanged: () => void;
}

export default function CategoryManager({ type, categories, cycles, onChanged }: Props) {
  const { notify } = useToast();
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<FinanceCategory | null>(null);
  const [form, setForm] = useState({ name: "", cycleId: "", icon: ICONS[0] });
  const [toDelete, setToDelete] = useState<FinanceCategory | null>(null);

  const list = categories.filter((c) => c.type === type);

  const openForm = (cat: FinanceCategory | null) => {
    setEditing(cat);
    setForm(
      cat
        ? { name: cat.name, cycleId: cat.cycleId, icon: cat.icon }
        : { name: "", cycleId: cycles[0]?.id ?? "", icon: ICONS[0] },
    );
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      notify("Le nom de la catégorie est obligatoire.", "error");
      return;
    }
    if (editing) {
      await updateCategory(editing.id, { name: form.name.trim(), cycleId: form.cycleId, icon: form.icon });
      notify("Catégorie mise à jour.");
    } else {
      await addCategory({ name: form.name.trim(), type, cycleId: form.cycleId, icon: form.icon });
      notify(`Catégorie « ${form.name} » ajoutée.`);
    }
    onChanged();
    setOpen(false);
  };

  return (
    <Card>
      <SectionTitle
        icon="ri-price-tag-3-line"
        title={`Catégories — ${type === "recette" ? "Recettes" : "Dépenses"}`}
        subtitle={`${list.length} catégories par cycle`}
        action={
          <Button size="sm" variant="outline" icon="ri-add-line" onClick={() => openForm(null)} style={can("finance-categories:create") ? undefined : { display: "none" }}>
            Ajouter
          </Button>
        }
      />
      <div className="space-y-4">
        {cycles.map((cycle) => {
          const cats = list.filter((c) => c.cycleId === cycle.id);
          if (cats.length === 0) return null;
          return (
            <div key={cycle.id}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                {cycle.label}
              </p>
              <div className="space-y-1.5">
                {cats.map((cat) => (
                  <div
                    key={cat.id}
                    className="flex items-center justify-between gap-2 rounded-md border border-background-200 bg-background-100/50 px-3 py-2"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="w-7 h-7 rounded-md bg-background-50 border border-background-200 flex items-center justify-center text-foreground-600 shrink-0">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      <span className="text-sm text-foreground-800 truncate">{cat.name}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => openForm(cat)}
                        className="press w-9 h-9 sm:w-7 sm:h-7 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer"
                        aria-label="Modifier la catégorie"
                        style={can("finance-categories:update") ? undefined : { display: "none" }}
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => setToDelete(cat)}
                        className="press w-9 h-9 sm:w-7 sm:h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 transition-colors cursor-pointer"
                        aria-label="Supprimer la catégorie"
                        style={can("finance-categories:delete") ? undefined : { display: "none" }}
                      >
                        <i className="ri-delete-bin-6-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Modifier la catégorie" : "Nouvelle catégorie"}
        icon="ri-price-tag-3-line"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button icon="ri-save-3-line" onClick={handleSave}>
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Nom de la catégorie" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Select label="Cycle" value={form.cycleId} onChange={(e) => setForm({ ...form, cycleId: e.target.value })}>
            {cycles.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
          <div>
            <p className="mb-2 text-xs font-medium text-foreground-700">Icône</p>
            <div className="flex flex-wrap gap-2">
              {ICONS.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => setForm({ ...form, icon })}
                  className={`w-9 h-9 flex items-center justify-center rounded-md border transition-colors cursor-pointer ${
                    form.icon === icon ? "border-primary-400 bg-primary-50 text-primary-700" : "border-background-300 text-foreground-500 hover:bg-background-100"
                  }`}
                  aria-label={icon}
                >
                  <i className={`${icon} text-lg`}></i>
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={toDelete !== null}
        title="Supprimer la catégorie"
        message={`Supprimer « ${toDelete?.name} » ? Les opérations liées à cette catégorie seront également retirées.`}
        confirmLabel="Supprimer"
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (toDelete) {
            await deleteCategory(toDelete.id);
            onChanged();
            notify("Catégorie supprimée.", "info");
          }
        }}
      />
    </Card>
  );
}